import { useMemo } from 'react';

import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { COLOR, FONT } from '@/shared/design-tokens/design-tokens';
import { formatMetricValue } from '@/shared/lib/utils/number';

import type { GpuPowerMetric } from '@/features/monitoring/api/types';

import styles from './GpuPowerGauge.module.css';

type GpuPowerGaugeProps = {
  data: GpuPowerMetric;
  height?: number;
};

const MAX_POWER = 300;
const WARNING_POWER = 200;
const DANGER_POWER = 280;

export const GpuPowerGauge = ({ data, height = 120 }: GpuPowerGaugeProps) => {
  const chartOption: EChartsOption = useMemo(() => {
    const getColor = () => {
      if (data.powerUsage >= DANGER_POWER) return COLOR.RED[50];
      if (data.powerUsage >= WARNING_POWER) return COLOR.ORANGE[50];
      return COLOR.GREEN[50];
    };

    return {
      series: [
        {
          type: 'gauge',
          radius: '100%',
          center: ['50%', '70%'],
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: MAX_POWER,
          itemStyle: {
            color: getColor(),
            shadowColor: `${getColor()}80`,
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
          progress: {
            show: true,
            width: 18,
          },
          pointer: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              width: 18,
              color: [[1, COLOR.GRAY[90]]],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          anchor: {
            show: false,
          },
          title: {
            show: false,
          },
          detail: {
            valueAnimation: true,
            width: '60%',
            lineHeight: 40,
            borderRadius: 8,
            offsetCenter: [0, '-5%'],
            fontSize: FONT.SIZE.LG,
            fontWeight: FONT.WEIGHT.EXTRA_BOLD,
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            formatter: () => `${formatMetricValue(data.powerUsage)}W`,
            color: getColor(),
          },
          data: [
            {
              value: data.powerUsage,
            },
          ],
        },
        {
          type: 'gauge',
          radius: '100%',
          center: ['50%', '70%'],
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: MAX_POWER,
          itemStyle: {
            color: 'transparent',
          },
          progress: {
            show: false,
          },
          pointer: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              width: 18,
              color: [
                [WARNING_POWER / MAX_POWER, `${COLOR.GREEN[50]}25`],
                [DANGER_POWER / MAX_POWER, `${COLOR.ORANGE[50]}25`],
                [1, `${COLOR.RED[50]}25`],
              ],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          detail: {
            show: false,
          },
          data: [{ value: 0 }],
        },
      ],
    };
  }, [data.powerUsage]);

  return (
    <div className={styles.gaugeContainer}>
      <div className={styles.gaugeWrapper}>
        <ReactECharts
          option={chartOption}
          style={{ height: `${height}px`, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
        />
      </div>
      <div className={styles.gpuLabel}>{data.displayName}</div>
    </div>
  );
};
